//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { AlertCTO } from '@shared/AlertCTO'
import { logOT } from '@shared/loggingShared'

import { TransactionCTO, TransactionStatus } from '@shared/TransactionCTO'

const PREFIX = 'alertActions'

// Define a type for the slice state
export interface AlertState {
  alerts: AlertCTO[]
  modalMessage: string
  modalPage: string
  pendingTransaction?: TransactionCTO
}

const localStorageKey = 'processingTransaction'

let pendingTransaction: TransactionCTO | undefined = undefined
try {
  pendingTransaction = localStorage?.getItem(localStorageKey)
    ? JSON.parse(localStorage?.getItem(localStorageKey) as string)
    : undefined
} catch (error) {
  logOT.error('Error parsing pending transaction', error)
}

// Define the initial state using that type
export const initialAlertState: AlertState = {
  alerts: [],
  modalPage: '',
  modalMessage: '',
  pendingTransaction,
}

function isValidPendingTx(tx: Partial<TransactionCTO>): boolean {
  // Adjust these checks as needed for your app
  const hasId = tx.id
  const hasName = !!tx.eventName
  const hasSender = !!tx.sender
  return Boolean(hasId && hasName && hasSender)
}

export const AlertSlice = createSlice({
  name: PREFIX,
  initialState: initialAlertState,
  reducers: {
    presignedTransaction: (state: Draft<AlertState>, action: PayloadAction<Partial<TransactionCTO>>) => {
      logOT.info('presignedTransaction', action.payload.methodName)
      action.payload.status = TransactionStatus.Presigned
      if (isValidPendingTx(action.payload)) {
        state.pendingTransaction = action.payload as TransactionCTO
        const txString = JSON.stringify(action.payload)
        localStorage.setItem(localStorageKey, txString)
      } else {
        // Optionally, clear any existing invalid tx
        localStorage.removeItem(localStorageKey)
        state.pendingTransaction = undefined
      }
    },
    signedTransaction: (state: Draft<AlertState>, action: PayloadAction<Partial<TransactionCTO>>) => {
      logOT.info('signedTransaction', action.payload.methodName)
      action.payload.status = TransactionStatus.Signed
      if (isValidPendingTx(action.payload)) {
        state.pendingTransaction = action.payload as TransactionCTO
        const txString = JSON.stringify(action.payload)
        localStorage.setItem(localStorageKey, txString)
      } else {
        localStorage.removeItem(localStorageKey)
        state.pendingTransaction = undefined
      }
    },
    minedTransaction: (state: Draft<AlertState>, action: PayloadAction<Partial<TransactionCTO>>) => {
      logOT.info('minedTransaction', action.payload.methodName)
      action.payload.status = TransactionStatus.Mined
      action.payload.minedTimestamp = Date.now()
      state.pendingTransaction = action.payload as TransactionCTO

      localStorage.removeItem(localStorageKey)
    },
    addAlert: (state: Draft<AlertState>, action: PayloadAction<AlertCTO>) => {
      state.alerts.push(action.payload)
    },
    clearAlerts: (state: Draft<AlertState>) => {
      state.alerts = []
    },
    setModalMessage: (state: Draft<AlertState>, action: PayloadAction<{ page: string; message: string }>) => {
      state.modalMessage = action.payload.message
      state.modalPage = action.payload.page
    },
    clearPendingTransaction: (state: Draft<AlertState>) => {
      state.pendingTransaction = undefined
      localStorage.removeItem(localStorageKey)
    },
  },
})

export const alertActions = { ...AlertSlice.actions }

// export const selectUsdcBalance = (state: RootState): number | undefined => state.web3State.usdcBalance
//
export const AlertReducer = AlertSlice.reducer
export default AlertSlice
